/**
 * Forced password change (issue #295).
 *
 * Shown to a signed-in account whose password was minted by an admin — via
 * provisioning or a reset — and not yet replaced by its holder. Until they
 * replace it, the admin who typed it out still knows it, and the API
 * confines the session to `POST /api/auth/change-password` (see
 * `api/src/auth/middleware.ts`); every other call 403s. So this screen is
 * not advice, it is the only thing the session can currently do.
 *
 * It is NOT a route. `ProtectedRoute` renders it in place of whatever the
 * user navigated to, which is what makes it unskippable: there is no URL to
 * type past it, and no protected page — present or future — that has to
 * remember to check. See `components/ProtectedRoute.jsx`.
 *
 * Deliberately plain, and deliberately not part of sign-in: nobody should
 * meet a wall of product on the screen where they are choosing a password.
 */
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { claimSessionTransition } from "../lib/sessionExpiry";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/** Mirrors `PASSWORD_MIN_LENGTH` in `api/src/auth/passwordPolicy.ts`. */
const PASSWORD_MIN_LENGTH = 10;

export default function ForcePasswordChange() {
  const { user, changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    // Checked here as well as server-side. The confirm field has no server
    // equivalent at all (there is nothing for the API to compare against),
    // so this is the only place a typo in a password nobody can see gets
    // caught before it becomes the credential.
    if (newPassword !== confirmPassword) {
      setError("The two new passwords don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("Choose a password different from the one you were given.");
      return;
    }
    setError("");
    setBusy(true);
    const result = await changePassword(currentPassword, newPassword);
    setBusy(false);
    if (result.ok) {
      toast.success("Password changed — you're all set.");
      return; // context now has must_change_password: false; the gate lifts.
    }
    setError(result.error);
  };

  // Issue #326. `logout` used to be wired straight to `onClick`, which was
  // fine while it always "succeeded": setting the context user to false made
  // `ProtectedRoute` render `<Navigate to="/login">` and this screen went
  // away. Now that a failed POST leaves the session intact and the user in
  // context, nothing would happen at all — a dead button on the one screen
  // whose whole purpose is to not be a dead end. So the failure gets the
  // same inline `force-password-error` the form uses; there is no toast on
  // this screen and it should not gain one for this.
  //
  // Still `logout()` and not `signOutAndGoHome`: this session is confined to
  // /auth/change-password, and the redirect to /login that clearing the user
  // triggers is the right destination for someone who cannot get in.
  //
  // Issue #436 item 5 — but it must still CLAIM the session transition, the
  // same way `signOut.js` does and for the same reason (#396). The moment
  // the server deletes the session row, anything this page still has in
  // flight comes back 401, and the interceptor on the shared instance would
  // take that first 401 to `/login?session=expired` — telling somebody who
  // just pressed "Sign out" that their session ended unexpectedly. That is
  // the sentence `signOut.js` exists to prevent, reached by the one sign-out
  // path that was not going through it. Claimed BEFORE the request, because
  // the gap being closed is between the server deleting the row and this
  // line hearing about it; released on failure, because a failed sign-out
  // means the session is still alive and a genuine expiry later in this same
  // page load must still be able to act on its own 401.
  const doSignOut = async () => {
    setError("");
    const releaseSessionTransition = claimSessionTransition();
    const result = await logout();
    if (result?.ok === false) {
      releaseSessionTransition();
      setError(result.error);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
      data-testid="force-password-change-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg px-3 py-2 inline-flex mb-6">
          <CompassLogo size={44} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h1 className="font-display font-black text-2xl text-slate-900">Choose your own password</h1>
          <p className="text-slate-500 mt-2 text-sm">
            Your account was set up for you, so the password you signed in with is one someone else
            has seen. Pick your own to finish setting up — it's the last step.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="force-password-change-form">
            <div>
              <Label>The password you were given</Label>
              <Input
                data-testid="force-current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label>New password</Label>
              <Input
                data-testid="force-new-password"
                type="password"
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="mt-1.5 h-11"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                At least {PASSWORD_MIN_LENGTH} characters.
              </p>
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                data-testid="force-confirm-password"
                type="password"
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-1.5 h-11"
              />
            </div>

            {error ? (
              <p className="text-sm text-red-600" data-testid="force-password-error" role="alert">
                {error}
              </p>
            ) : null}

            <Button
              data-testid="force-password-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 text-base font-semibold"
              style={{ background: "#0B2E59" }}
            >
              {busy ? "Saving…" : "Save password"}
            </Button>
          </form>

          {/* Signing out is the one other thing a confined session can do
              (the API keeps /auth/logout reachable on purpose), and offering
              it here means someone who cannot find the note with their
              password is not stuck on a screen with no exit. */}
          <div className="mt-6 text-sm text-center text-slate-600">
            {user?.email ? <span className="block mb-2 text-slate-400">Signed in as {user.email}</span> : null}
            <button
              type="button"
              onClick={doSignOut}
              className="font-semibold text-slate-500 hover:text-slate-900 underline"
              data-testid="force-password-signout"
            >
              Sign out instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
